import { useState } from 'react';

// ── HELPERS ──────────────────────────────────────────────────────────────────

function fmtShort(n) {
  if (n >= 1000000) return '$' + (n / 1000000).toFixed(2) + 'M';
  if (n >= 1000) return '$' + Math.round(n / 1000) + 'K';
  return '$' + n;
}

function fmtFull(n) {
  return '$' + Math.round(n).toLocaleString();
}

function polarToCart(cx, cy, r, deg) {
  const rad = ((deg - 90) * Math.PI) / 180;
  return [+(cx + r * Math.cos(rad)).toFixed(3), +(cy + r * Math.sin(rad)).toFixed(3)];
}

function donutPath(cx, cy, R, ri, a1, a2) {
  const [x1, y1] = polarToCart(cx, cy, R, a1);
  const [x2, y2] = polarToCart(cx, cy, R, a2);
  const [x3, y3] = polarToCart(cx, cy, ri, a2);
  const [x4, y4] = polarToCart(cx, cy, ri, a1);
  const lg = a2 - a1 > 180 ? 1 : 0;
  return `M ${x1} ${y1} A ${R} ${R} 0 ${lg} 1 ${x2} ${y2} L ${x3} ${y3} A ${ri} ${ri} 0 ${lg} 0 ${x4} ${y4} Z`;
}

// ── STATIC DATA ───────────────────────────────────────────────────────────────

const MONTHLY = [
  { m: 'Jan', rev: 125400, ord: 89 },
  { m: 'Feb', rev: 118200, ord: 82 },
  { m: 'Mar', rev: 142800, ord: 97 },
  { m: 'Apr', rev: 156100, ord: 108 },
  { m: 'May', rev: 148900, ord: 103 },
  { m: 'Jun', rev: 163400, ord: 114 },
  { m: 'Jul', rev: 171200, ord: 119 },
  { m: 'Aug', rev: 158600, ord: 110 },
  { m: 'Sep', rev: 182300, ord: 127 },
  { m: 'Oct', rev: 195800, ord: 136 },
  { m: 'Nov', rev: 210400, ord: 146 },
  { m: 'Dec', rev: 187600, ord: 130 },
];

const QMAP = { Q1: [0, 3], Q2: [3, 6], Q3: [6, 9], Q4: [9, 12], 'Full Year': [0, 12] };

const PRODUCTS = [
  { label: 'Product A',    rev: 763200, color: '#F5A623' },
  { label: 'Product B',    rev: 481000, color: '#2C4A6E' },
  { label: 'Service Pkg',  rev: 360800, color: '#34d399' },
  { label: 'Consulting',   rev: 260700, color: '#60a5fa' },
  { label: 'Support Plan', rev: 140300, color: '#a78bfa' },
];

const REGIONS = [
  { label: 'Southeast', pct: 0.31, color: '#F5A623' },
  { label: 'Northeast', pct: 0.22, color: '#2C4A6E' },
  { label: 'Midwest',   pct: 0.19, color: '#34d399' },
  { label: 'West',      pct: 0.17, color: '#60a5fa' },
  { label: 'Southwest', pct: 0.11, color: '#a78bfa' },
];

const ORDERS = [
  { id: '#10092', customer: 'M. Johnson',  email: 'm.jo****@gmail.com',    region: 'Southeast', product: 'Product A',    amount: 2840, date: 'Dec 15', status: 'Completed'  },
  { id: '#10091', customer: 'T. Williams', email: 't.wi****@outlook.com',  region: 'Northeast', product: 'Consulting',   amount: 4200, date: 'Dec 14', status: 'Completed'  },
  { id: '#10090', customer: 'A. Martinez', email: 'a.ma****@yahoo.com',    region: 'West',      product: 'Product B',    amount:  890, date: 'Dec 13', status: 'Processing' },
  { id: '#10089', customer: 'R. Thompson', email: 'r.th****@gmail.com',    region: 'Midwest',   product: 'Service Pkg',  amount: 1950, date: 'Dec 12', status: 'Completed'  },
  { id: '#10088', customer: 'S. Davis',    email: 's.da****@company.com',  region: 'Southwest', product: 'Support Plan', amount:  600, date: 'Dec 11', status: 'Pending'    },
  { id: '#10087', customer: 'L. Anderson', email: 'l.an****@gmail.com',    region: 'Southeast', product: 'Product A',    amount: 2840, date: 'Dec 10', status: 'Completed'  },
  { id: '#10086', customer: 'C. Wilson',   email: 'c.wi****@biz.net',      region: 'Northeast', product: 'Consulting',   amount: 5600, date: 'Dec 9',  status: 'Completed'  },
  { id: '#10085', customer: 'P. Garcia',   email: 'p.ga****@hotmail.com',  region: 'West',      product: 'Product B',    amount:  890, date: 'Dec 8',  status: 'Completed'  },
  { id: '#10084', customer: 'D. Lee',      email: 'd.le****@gmail.com',    region: 'Midwest',   product: 'Service Pkg',  amount: 1950, date: 'Dec 7',  status: 'Completed'  },
  { id: '#10083', customer: 'B. Harris',   email: 'b.ha****@yahoo.com',    region: 'Southeast', product: 'Product A',    amount: 2840, date: 'Dec 6',  status: 'Completed'  },
];

// ── COMPONENT ─────────────────────────────────────────────────────────────────

export default function SalesDashboard() {
  const [period,   setPeriod]   = useState('Full Year');
  const [region,   setRegion]   = useState('All');
  const [hoverBar, setHoverBar] = useState(null);
  const [hoverPt,  setHoverPt]  = useState(null);
  const [hoverReg, setHoverReg] = useState(null);

  // ── Derived state ──────────────────────────────────────────────────────────
  const [qStart, qEnd] = QMAP[period];
  const monthSlice = MONTHLY.slice(qStart, qEnd);
  const regionMult = region === 'All' ? 1 : REGIONS.find(r => r.label === region).pct;

  const totalRev    = monthSlice.reduce((s, m) => s + m.rev, 0) * regionMult;
  const totalOrders = Math.round(monthSlice.reduce((s, m) => s + m.ord, 0) * regionMult);
  const avgOrder    = totalOrders ? totalRev / totalOrders : 0;
  const topProduct  = PRODUCTS[0];

  const visibleOrders = region === 'All'
    ? ORDERS
    : ORDERS.filter(o => o.region === region);
  const tableRows = (visibleOrders.length > 0 ? visibleOrders : ORDERS).slice(0, 6);

  // ── Line chart geometry ────────────────────────────────────────────────────
  const LC = { vw: 560, vh: 148, pl: 56, pr: 14, pt: 16, pb: 32 };
  const lcW  = LC.vw - LC.pl - LC.pr;
  const lcH  = LC.vh - LC.pt - LC.pb;
  const maxRevScale = 230000;
  const lcX = i => LC.pl + (monthSlice.length > 1 ? (i / (monthSlice.length - 1)) * lcW : lcW / 2);
  const lcY = v => LC.pt + lcH - (v / maxRevScale) * lcH;
  const linePoints = monthSlice.map((d, i) => `${lcX(i)},${lcY(d.rev)}`).join(' ');
  const areaPoints = monthSlice.length > 0
    ? `${LC.pl},${LC.pt + lcH} ${monthSlice.map((d, i) => `${lcX(i)},${lcY(d.rev)}`).join(' ')} ${lcX(monthSlice.length - 1)},${LC.pt + lcH}`
    : '';

  // ── Bar chart geometry ─────────────────────────────────────────────────────
  const BC = { vw: 460, vh: 148, pl: 50, pr: 12, pt: 16, pb: 32 };
  const bcW   = BC.vw - BC.pl - BC.pr;
  const bcH   = BC.vh - BC.pt - BC.pb;
  const maxProd = PRODUCTS[0].rev * 1.12;
  const bw    = (bcW - (PRODUCTS.length - 1) * 10) / PRODUCTS.length;
  const bcX   = i => BC.pl + i * (bw + 10);
  const bcBarH = v => (v / maxProd) * bcH;

  // ── Donut geometry ─────────────────────────────────────────────────────────
  const DC = { cx: 88, cy: 88, R: 68, ri: 44 };
  let angle = 0;
  const segments = REGIONS.map(reg => {
    const startDeg = angle;
    const endDeg   = angle + reg.pct * 360;
    angle = endDeg;
    return { ...reg, startDeg, endDeg, path: donutPath(DC.cx, DC.cy, DC.R, DC.ri, startDeg, endDeg) };
  });

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div style={s.wrap}>

      {/* HEADER */}
      <div style={s.header}>
        <div style={s.headerLeft}>
          <span style={s.demoTag}>DEMO</span>
          <span style={s.headerTitle}>Sample Sales Overview</span>
        </div>
        <div style={s.filters}>
          <span style={s.filterLabel}>Period:</span>
          {Object.keys(QMAP).map(p => (
            <button key={p} onClick={() => setPeriod(p)}
              style={{ ...s.filterBtn, ...(period === p ? s.filterActive : {}) }}>
              {p}
            </button>
          ))}
          <span style={{ ...s.filterLabel, marginLeft: '10px' }}>Region:</span>
          {['All', ...REGIONS.map(r => r.label)].map(r => {
            const short = { Southeast: 'SE', Northeast: 'NE', Midwest: 'MW', West: 'West', Southwest: 'SW', All: 'All' }[r] || r;
            return (
              <button key={r} onClick={() => setRegion(r)}
                style={{ ...s.filterBtn, ...(region === r ? s.filterActive : {}) }}>
                {short}
              </button>
            );
          })}
        </div>
      </div>

      {/* KPI CARDS */}
      <div style={s.kpiRow}>
        {[
          { label: 'Total Revenue',   value: fmtFull(totalRev),            sub: `${monthSlice.length} month${monthSlice.length !== 1 ? 's' : ''}` },
          { label: 'Total Orders',    value: totalOrders.toLocaleString(),  sub: 'transactions' },
          { label: 'Avg Order Value', value: fmtFull(avgOrder),             sub: 'per transaction' },
          { label: 'Top Product',     value: topProduct.label,              sub: `${fmtShort(topProduct.rev * regionMult)} revenue` },
        ].map((k, i) => (
          <div key={i} style={{ ...s.kpiCard, borderRight: i < 3 ? '1px solid #E2E8F0' : 'none' }}>
            <div style={s.kpiLabel}>{k.label}</div>
            <div style={s.kpiValue}>{k.value}</div>
            <div style={s.kpiSub}>{k.sub}</div>
          </div>
        ))}
      </div>

      {/* CHARTS ROW 1 */}
      <div style={s.row}>

        {/* Line Chart – Revenue Trend */}
        <div style={s.card}>
          <div style={s.chartTitle}>Revenue Trend</div>
          <svg viewBox={`0 0 ${LC.vw} ${LC.vh}`} style={{ width: '100%', overflow: 'visible' }}>
            <defs>
              <linearGradient id="lineGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#F5A623" stopOpacity="0.18" />
                <stop offset="100%" stopColor="#F5A623" stopOpacity="0" />
              </linearGradient>
            </defs>
            {/* Y-axis grid + labels */}
            {[0, 0.25, 0.5, 0.75, 1].map(f => {
              const y = LC.pt + lcH - f * lcH;
              const v = Math.round(f * maxRevScale);
              return (
                <g key={f}>
                  <line x1={LC.pl} y1={y} x2={LC.vw - LC.pr} y2={y} stroke="#E5E7EB" strokeWidth="1" />
                  <text x={LC.pl - 5} y={y + 4} textAnchor="end" fontSize="9" fill="#9CA3AF">
                    {v >= 1000 ? `$${v / 1000}K` : `$${v}`}
                  </text>
                </g>
              );
            })}
            {/* Area fill */}
            {areaPoints && <polygon points={areaPoints} fill="url(#lineGrad)" />}
            {/* Line */}
            {monthSlice.length > 1 && (
              <polyline points={linePoints} fill="none" stroke="#F5A623" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
            )}
            {/* Data points + hover */}
            {monthSlice.map((d, i) => (
              <g key={i} style={{ cursor: 'pointer' }}
                onMouseEnter={() => setHoverPt(i)}
                onMouseLeave={() => setHoverPt(null)}>
                {/* Invisible wide hit area */}
                <rect x={lcX(i) - 14} y={LC.pt} width="28" height={lcH} fill="transparent" />
                {/* Guide line on hover */}
                {hoverPt === i && (
                  <line x1={lcX(i)} y1={LC.pt} x2={lcX(i)} y2={LC.pt + lcH}
                    stroke="#F5A623" strokeWidth="1" strokeDasharray="3 3" />
                )}
                {/* Dot */}
                <circle cx={lcX(i)} cy={lcY(d.rev)} r={hoverPt === i ? 5 : 3.5}
                  fill={hoverPt === i ? '#F5A623' : 'white'} stroke="#F5A623" strokeWidth="2" />
                {/* Tooltip */}
                {hoverPt === i && (
                  <g>
                    <rect x={Math.min(lcX(i) - 34, LC.vw - LC.pr - 68)} y={lcY(d.rev) - 30}
                      width="68" height="20" rx="5" fill="#1A2E44" />
                    <text x={Math.min(lcX(i), LC.vw - LC.pr - 34)} y={lcY(d.rev) - 16}
                      textAnchor="middle" fontSize="9" fill="white" fontWeight="700">
                      {d.m}: {fmtShort(d.rev)}
                    </text>
                  </g>
                )}
                {/* X label */}
                <text x={lcX(i)} y={LC.pt + lcH + 14} textAnchor="middle" fontSize="9" fill="#9CA3AF">{d.m}</text>
              </g>
            ))}
          </svg>
        </div>

        {/* Bar Chart – Product Revenue */}
        <div style={s.card}>
          <div style={s.chartTitle}>Revenue by Product</div>
          <svg viewBox={`0 0 ${BC.vw} ${BC.vh}`} style={{ width: '100%', overflow: 'visible' }}>
            {/* Grid */}
            {[0, 0.25, 0.5, 0.75, 1].map(f => {
              const y = BC.pt + bcH - f * bcH;
              const v = Math.round(f * maxProd);
              return (
                <g key={f}>
                  <line x1={BC.pl} y1={y} x2={BC.vw - BC.pr} y2={y} stroke="#E5E7EB" strokeWidth="1" />
                  <text x={BC.pl - 5} y={y + 4} textAnchor="end" fontSize="9" fill="#9CA3AF">
                    {v >= 1000 ? `$${Math.round(v / 1000)}K` : `$${v}`}
                  </text>
                </g>
              );
            })}
            {/* Bars */}
            {PRODUCTS.map((p, i) => {
              const bh = bcBarH(p.rev);
              const x  = bcX(i);
              const y  = BC.pt + bcH - bh;
              const dimmed = hoverBar !== null && hoverBar !== i;
              return (
                <g key={i} style={{ cursor: 'pointer' }}
                  onMouseEnter={() => setHoverBar(i)}
                  onMouseLeave={() => setHoverBar(null)}>
                  <rect x={x} y={y} width={bw} height={bh} rx="4"
                    fill={p.color} opacity={dimmed ? 0.3 : 1} />
                  {hoverBar === i && (
                    <g>
                      <rect x={x + bw / 2 - 30} y={y - 22} width="60" height="18" rx="4" fill="#1A2E44" />
                      <text x={x + bw / 2} y={y - 9} textAnchor="middle" fontSize="9" fill="white" fontWeight="700">
                        {fmtShort(p.rev)}
                      </text>
                    </g>
                  )}
                  <text x={x + bw / 2} y={BC.pt + bcH + 14} textAnchor="middle" fontSize="8.5" fill="#9CA3AF">
                    {p.label}
                  </text>
                </g>
              );
            })}
          </svg>
        </div>

      </div>

      {/* CHARTS ROW 2 */}
      <div style={s.row}>

        {/* Donut – Region Mix */}
        <div style={{ ...s.card, flex: '0 0 auto', minWidth: '260px' }}>
          <div style={s.chartTitle}>Sales by Region</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '18px' }}>
            <svg viewBox="0 0 176 176" style={{ width: '158px', flexShrink: 0 }}>
              {segments.map((seg, i) => (
                <path key={i} d={seg.path}
                  fill={seg.color}
                  stroke="white" strokeWidth={hoverReg === i ? 2.5 : 1}
                  opacity={hoverReg === null || hoverReg === i ? 1 : 0.3}
                  style={{ cursor: 'pointer', transition: 'opacity 0.15s' }}
                  onMouseEnter={() => setHoverReg(i)}
                  onMouseLeave={() => setHoverReg(null)}
                />
              ))}
              {/* Center */}
              <text x={DC.cx} y={DC.cy - 4} textAnchor="middle" fontSize="18" fontWeight="800" fill="#1A2E44">
                {hoverReg !== null ? Math.round(REGIONS[hoverReg].pct * 100) + '%' : '5'}
              </text>
              <text x={DC.cx} y={DC.cy + 12} textAnchor="middle" fontSize="9" fill="#6B7280">
                {hoverReg !== null ? REGIONS[hoverReg].label : 'Regions'}
              </text>
            </svg>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: 1 }}>
              {REGIONS.map((r, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '7px', cursor: 'pointer', opacity: hoverReg === null || hoverReg === i ? 1 : 0.35, transition: 'opacity 0.15s' }}
                  onMouseEnter={() => setHoverReg(i)}
                  onMouseLeave={() => setHoverReg(null)}>
                  <div style={{ width: '9px', height: '9px', borderRadius: '2px', background: r.color, flexShrink: 0 }} />
                  <span style={{ fontSize: '0.79rem', color: '#374151', fontWeight: 500, flex: 1 }}>{r.label}</span>
                  <span style={{ fontSize: '0.79rem', color: '#9CA3AF', fontWeight: 600 }}>{Math.round(r.pct * 100)}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Orders Table – Obfuscated PII */}
        <div style={{ ...s.card, flex: 1, overflowX: 'auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
            <div style={s.chartTitle}>Recent Orders</div>
            <div style={s.piiNote}>
              <span style={{ marginRight: '4px' }}>&#128274;</span>
              Customer data anonymized — names &amp; emails are obfuscated
            </div>
          </div>
          <table style={s.table}>
            <thead>
              <tr>
                {['Order', 'Customer', 'Email', 'Product', 'Region', 'Amount', 'Date', 'Status'].map(h => (
                  <th key={h} style={s.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tableRows.map((o, i) => (
                <tr key={i} style={{ background: i % 2 === 0 ? 'white' : '#F8FAFC' }}>
                  <td style={{ ...s.td, color: '#9CA3AF', fontSize: '0.78rem' }}>{o.id}</td>
                  <td style={{ ...s.td, fontWeight: 600 }}>{o.customer}</td>
                  <td style={{ ...s.td, color: '#9CA3AF', fontStyle: 'italic', fontSize: '0.78rem' }}>{o.email}</td>
                  <td style={s.td}>{o.product}</td>
                  <td style={s.td}>{o.region}</td>
                  <td style={{ ...s.td, fontWeight: 700, color: '#1A2E44' }}>${o.amount.toLocaleString()}</td>
                  <td style={{ ...s.td, color: '#6B7280' }}>{o.date}</td>
                  <td style={s.td}>
                    <span style={{
                      ...s.badge,
                      background: o.status === 'Completed'  ? '#DCFCE7' : o.status === 'Processing' ? '#FEF9C3' : '#FEE2E2',
                      color:      o.status === 'Completed'  ? '#166534' : o.status === 'Processing' ? '#854D0E' : '#991B1B',
                    }}>
                      {o.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

      </div>

      {/* FOOTER */}
      <div style={s.footerNote}>
        * All data shown is fictional and for demonstration purposes only. Your dashboard would display your real business data, securely hosted and accessible only to you.
      </div>

    </div>
  );
}

const s = {
  wrap:         { background: '#EEF2F7', borderRadius: '16px', overflow: 'hidden', border: '1px solid #D1D9E4', fontFamily: 'inherit' },
  header:       { background: '#1A2E44', padding: '13px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap' },
  headerLeft:   { display: 'flex', alignItems: 'center', gap: '10px' },
  demoTag:      { background: '#F5A623', color: '#1A2E44', fontSize: '0.62rem', fontWeight: 800, padding: '2px 8px', borderRadius: '4px', letterSpacing: '0.09em' },
  headerTitle:  { color: 'white', fontWeight: 700, fontSize: '0.93rem' },
  filters:      { display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' },
  filterLabel:  { color: 'rgba(255,255,255,0.5)', fontSize: '0.72rem', fontWeight: 500, marginRight: '2px' },
  filterBtn:    { background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.75)', padding: '4px 10px', borderRadius: '6px', fontSize: '0.72rem', cursor: 'pointer', fontWeight: 500 },
  filterActive: { background: '#F5A623', border: '1px solid #F5A623', color: '#1A2E44', fontWeight: 700 },
  kpiRow:       { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', background: 'white', borderBottom: '1px solid #E2E8F0' },
  kpiCard:      { padding: '18px 22px' },
  kpiLabel:     { fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#9CA3AF', marginBottom: '5px' },
  kpiValue:     { fontSize: '1.4rem', fontWeight: 800, color: '#1A2E44', lineHeight: 1.1, marginBottom: '3px' },
  kpiSub:       { fontSize: '0.72rem', color: '#9CA3AF' },
  row:          { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1px', background: '#D1D9E4', borderTop: '1px solid #D1D9E4' },
  card:         { background: 'white', padding: '18px 18px 14px' },
  chartTitle:   { fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#9CA3AF', marginBottom: '12px' },
  piiNote:      { fontSize: '0.7rem', color: '#9CA3AF', fontWeight: 500 },
  table:        { width: '100%', borderCollapse: 'collapse', fontSize: '0.81rem' },
  th:           { textAlign: 'left', padding: '6px 10px', fontSize: '0.67rem', fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '2px solid #E5E7EB', whiteSpace: 'nowrap' },
  td:           { padding: '8px 10px', color: '#374151', borderBottom: '1px solid #F3F4F6', whiteSpace: 'nowrap' },
  badge:        { padding: '2px 9px', borderRadius: '20px', fontSize: '0.7rem', fontWeight: 600, display: 'inline-block' },
  footerNote:   { background: '#F8FAFC', borderTop: '1px solid #E2E8F0', padding: '9px 20px', fontSize: '0.72rem', color: '#9CA3AF', fontStyle: 'italic' },
};
